/*
 * Kopi Blok — keep paid add-on lines in step with the item they belong to.
 *
 * A surcharge such as "3 דמויות (+25 ₪)" is charged as a separate cart line of a hidden add-on
 * product (product_type תוספות), added by the product form. Nothing in Shopify ties the two lines
 * together, so editing the cart broke the price: raising the item's quantity left the add-on where
 * it was, removing the add-on made the order cheaper than chosen, and removing the item left an
 * orphan add-on that charged for nothing.
 *
 * This file reads /cart.js after every cart change — the cart page, the Essential Cart Drawer, a
 * reload or a back-navigation — and corrects it to one rule:
 *     add-on quantity = units per item × item quantity, and no add-on without its item.
 *
 * How a line finds its item:
 *   - New lines carry hidden properties set by the product form: _kb_link (same id on the item and
 *     its add-ons), _kb_per (units per item, on the add-on) and _kb_addons (on the item: which
 *     add-ons it needs, so a removed add-on can be put back).
 *   - Older lines have only עבור (product title) + הבחירה (the chosen option text). They are matched
 *     only when exactly one item fits, and units come from the "(+NN ₪)" in the choice. When it is
 *     ambiguous nothing is touched — a wrong guess would change someone's order.
 *
 * Every write is a single /cart/update.js or /cart/add.js call marked ?kb_guard=1, so the guard
 * ignores its own requests. It never runs while the product form is adding (window.__kbCartBusy).
 */
(function () {
  if (typeof window === 'undefined' || !window.fetch) return;

  var ADDON_TYPE = 'תוספות';
  var running = false, again = false, timer = null, passes = 0;

  function props(item) { return item.properties || {}; }
  function isAddon(item) { return item.product_type === ADDON_TYPE || Object.prototype.hasOwnProperty.call(props(item), 'עבור'); }
  function surcharge(text) {
    var t = String(text || '').replace(/,/g, '');
    var m = t.match(/\+\s*(\d+(?:[.,]\d+)?)/) || t.match(/בתוספת\s*(\d+(?:[.,]\d+)?)/);
    return m ? parseFloat(String(m[1]).replace(',', '.')) || 0 : 0;
  }
  function parseAddons(raw) {
    try { var a = JSON.parse(raw); return Array.isArray(a) ? a : []; } catch (e) { return []; }
  }
  function valuesOf(item) {
    var p = props(item), out = [];
    Object.keys(p).forEach(function (k) { if (k.charAt(0) !== '_') out.push(String(p[k])); });
    return out;
  }

  /* What the cart should look like. Returns { updates: {key: qty}, adds: [...], notes: [...] }. */
  function plan(cart) {
    var mains = cart.items.filter(function (i) { return !isAddon(i); });
    var addons = cart.items.filter(isAddon);
    var updates = {}, adds = [], notes = [];

    addons.forEach(function (a) {
      var p = props(a), parent = null, per = 0;
      if (p._kb_link) {
        parent = mains.filter(function (m) { return props(m)._kb_link === p._kb_link; })[0] || null;
        per = parseInt(p._kb_per, 10) || 0;
      } else {
        // Legacy line: title + chosen text must point at exactly one item.
        var title = String(p['עבור'] || ''), choice = String(p['הבחירה'] || '');
        var sameProduct = mains.filter(function (m) { return m.product_title === title || m.title === title; });
        var exact = sameProduct.filter(function (m) { return !props(m)._kb_link && valuesOf(m).indexOf(choice) !== -1; });
        if (exact.length > 1) return;                       // ambiguous — leave it alone
        if (exact.length === 0 && sameProduct.length) return; // the item is there, the link is unclear
        parent = exact[0] || null;
        var unit = (a.price || 0) / 100;
        per = unit ? Math.round(surcharge(choice) / unit) : 0;
      }
      if (parent && !per) return;                            // cannot tell how many — do not guess
      var want = parent ? per * parent.quantity : 0;
      if (a.quantity !== want) {
        updates[a.key] = want;
        notes.push(want === 0 ? 'removed' : 'quantity');
      }
    });

    // An item whose add-on line was removed gets it back, with the same visible properties.
    mains.forEach(function (m) {
      var p = props(m);
      if (!p._kb_link || !p._kb_addons) return;
      parseAddons(p._kb_addons).forEach(function (spec) {
        var have = addons.some(function (a) { return props(a)._kb_link === p._kb_link && String(a.variant_id) === String(spec.id); });
        if (have || !spec.id || !spec.per) return;
        adds.push({ id: spec.id, quantity: spec.per * m.quantity,
          properties: { 'עבור': m.product_title, 'הבחירה': spec.choice || '', '_kb_link': p._kb_link, '_kb_per': String(spec.per) } });
        notes.push('restored');
      });
    });
    return { updates: updates, adds: adds, notes: notes };
  }

  function getCart() {
    return fetch('/cart.js?kb_guard=1', { headers: { Accept: 'application/json' } }).then(function (r) {
      if (!r.ok) throw new Error('cart ' + r.status);
      return r.json();
    });
  }
  function post(url, body) {
    return fetch(url + '?kb_guard=1', {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body)
    }).then(function (r) { if (!r.ok) throw new Error(url + ' ' + r.status); return r.json(); });
  }

  var toastEl = null;
  function toast(msg) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      toastEl.style.cssText = 'position:fixed;inset-inline:16px;bottom:calc(16px + env(safe-area-inset-bottom,0px));z-index:2147483646;' +
        'max-width:420px;margin-inline:auto;background:#282020;color:#fff;font:15px/1.4 Assistant,Arial,sans-serif;' +
        'padding:12px 16px;border-radius:8px;box-shadow:0 4px 16px rgba(0,0,0,.2);text-align:right;direction:rtl;display:none';
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = msg;
    toastEl.style.display = 'block';
    clearTimeout(toastEl._t);
    toastEl._t = setTimeout(function () { toastEl.style.display = 'none'; }, 5000);
  }

  function refreshViews(notes) {
    var api = window.essentialCartApi;
    try { if (api && api.refreshCart) api.refreshCart(); } catch (e) { /* drawer refreshes on its next open */ }
    var msg = notes.indexOf('removed') !== -1 && notes.length === 1
      ? 'הסרנו מהסל תוספת שהמוצר שלה כבר לא בסל.'
      : 'התוספות בסל מתעדכנות לפי כמות המוצר, כדי שהמחיר יישאר נכון.';
    toast(msg);
    if (document.getElementById('main-cart-items')) rerenderCartPage();
  }

  // The cart page is rendered by the server. Re-render its lines, totals and the header count the
  // way Dawn itself does after a quantity change (Section Rendering API), so the corrected price
  // shows without a reload. A reload is the fallback — at most one per 15s, so a cart Shopify will
  // not settle (stock limits) cannot loop.
  function rerenderCartPage() {
    var parts = [
      { id: 'main-cart-items', selector: '.js-contents' },
      { id: 'main-cart-footer', selector: '.js-contents' },
      { id: 'cart-icon-bubble', selector: '.shopify-section', section: 'cart-icon-bubble' }
    ].map(function (s) {
      var el = document.getElementById(s.id);
      return el ? { el: el, selector: s.selector, section: s.section || el.dataset.id } : null;
    }).filter(function (s) { return s && s.section; });
    fetch(location.pathname + '?sections=' + parts.map(function (s) { return s.section; }).join(',') + '&kb_guard=1')
      .then(function (r) { if (!r.ok) throw new Error('sections ' + r.status); return r.json(); })
      .then(function (html) {
        parts.forEach(function (s) {
          if (!html[s.section]) return;
          var fresh = new DOMParser().parseFromString(html[s.section], 'text/html').querySelector(s.selector);
          var target = s.el.querySelector(s.selector) || s.el;
          if (fresh) target.innerHTML = fresh.innerHTML;
        });
      })
      .catch(function () {
        try {
          var last = parseInt(sessionStorage.getItem('kbGuardReload') || '0', 10);
          if (Date.now() - last < 15000) return;
          sessionStorage.setItem('kbGuardReload', String(Date.now()));
        } catch (e) { return; }
        location.reload();
      });
  }

  function run() {
    if (running) { again = true; return; }
    if (window.__kbCartBusy) { schedule(600); return; }
    running = true;
    getCart().then(function (cart) {
      var p = plan(cart);
      var hasUpdates = Object.keys(p.updates).length > 0;
      if (!hasUpdates && !p.adds.length) { passes = 0; return null; }
      if (++passes > 3) { console.warn('[kb-cart-guard] cart did not settle, giving up', p); return null; }
      var chain = Promise.resolve();
      if (hasUpdates) chain = chain.then(function () { return post('/cart/update.js', { updates: p.updates }); });
      if (p.adds.length) chain = chain.then(function () { return post('/cart/add.js', { items: p.adds }); });
      return chain.then(function () { refreshViews(p.notes); again = true; });
    }).catch(function (e) {
      console.warn('[kb-cart-guard]', e && e.message ? e.message : e);
    }).then(function () {
      running = false;
      if (again) { again = false; schedule(300); }
    });
  }
  function schedule(ms) { clearTimeout(timer); timer = setTimeout(run, ms == null ? 350 : ms); }

  // Every finished cart write that is not our own: Dawn's cart page, the drawer's +/-/remove,
  // the drawer's variant switch, other apps. Resource timing sees them whatever fetch made them.
  if ('PerformanceObserver' in window) {
    try {
      new PerformanceObserver(function (list) {
        list.getEntries().forEach(function (en) {
          if (/\/cart\/(add|change|update|clear)/.test(en.name) && en.name.indexOf('kb_guard=1') === -1) schedule();
        });
      }).observe({ type: 'resource', buffered: false });
    } catch (e) { /* older browsers: the load / pageshow checks below still run */ }
  }
  window.addEventListener('pageshow', function () { schedule(500); });
  if (document.readyState === 'complete') schedule(500); else window.addEventListener('load', function () { schedule(500); });

  window.kbCartGuard = { plan: plan, run: run };
})();
